import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  SEOData, 
  generatePageTitle, 
  generateMetaDescription, 
  generateOpenGraphTags, 
  generateTwitterCardTags, 
  generateCanonicalUrl 
} from '../../utils/seo';

const SITE_NAME = 'Nortiq.ca - Product Reviews';
const DEFAULT_DESCRIPTION = 'Discover amazing products with honest reviews, detailed guides, and trusted recommendations to enhance your lifestyle.';
const DEFAULT_KEYWORDS = 'product reviews, lifestyle guides, shopping recommendations, honest reviews';
const DEFAULT_IMAGE = '/logo512.png';
const TWITTER_HANDLE = '@nortiqca';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  noIndex?: boolean;
  noFollow?: boolean;
  canonicalUrl?: string;
  structuredData?: string;
  prevUrl?: string;
  nextUrl?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  section,
  tags,
  noIndex = false,
  noFollow = false,
  canonicalUrl,
  structuredData
  , prevUrl, nextUrl
}) => {
  const currentUrl = canonicalUrl || generateCanonicalUrl(window.location.pathname);
  
  // Compose final title - if title is empty, use siteName directly
  const finalTitle = title ? generatePageTitle(title, SITE_NAME) : SITE_NAME;
  
  const seoData: SEOData = {
    title: finalTitle,
    description: generateMetaDescription(description || DEFAULT_DESCRIPTION),
    keywords: keywords || DEFAULT_KEYWORDS,
    image: image || DEFAULT_IMAGE,
    url: currentUrl,
    type,
    siteName: SITE_NAME,
    author,
    publishedTime,
    modifiedTime,
    section,
    tags,
    twitterHandle: TWITTER_HANDLE
  };

  const openGraphTags = generateOpenGraphTags(seoData);
  const twitterCardTags = generateTwitterCardTags(seoData);

  // (production) no debug logs

  // Generate robots meta tag
  const robotsContent = [];
  if (noIndex) robotsContent.push('noindex');
  if (noFollow) robotsContent.push('nofollow');
  const robots = robotsContent.length > 0 ? robotsContent.join(', ') : 'index, follow';

  // (production) rely on Helmet for title updates

  return (
    <Helmet key={currentUrl} prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      {/* Pagination rel links */}
      {prevUrl && <link rel="prev" href={prevUrl} />}
      {nextUrl && <link rel="next" href={nextUrl} />}
      
      {/* Author */}
      {author && <meta name="author" content={author} />}
      
      {/* Article-specific meta tags */}
      {type === 'article' && (
        <>
          {publishedTime && <meta name="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta name="article:modified_time" content={modifiedTime} />}
          {author && <meta name="article:author" content={author} />}
          {section && <meta name="article:section" content={section} />}
          {tags && tags.map((tag, index) => (
            <meta key={`article-tag-${index}`} name="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Enhanced SEO meta tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
      {/* Open Graph Tags */}
      {openGraphTags.map((tag, index) => (
        <meta key={`og-${index}`} property={tag.property} content={tag.content} />
      ))}
      {/* rel=tag links for each post tag */}
      {type === 'article' && Array.isArray(tags) && tags.map((t, i) => {
        const slug = t.toLowerCase().replace(/\s+/g, '-');
        const href = generateCanonicalUrl(`/tag/${slug}`);
        return <link key={`rel-tag-${i}`} rel="tag" href={href} title={t} />;
      })}
      
      {/* Twitter Card Tags */}
      {twitterCardTags.map((tag, index) => (
        <meta key={`twitter-${index}`} name={tag.name} content={tag.content} />
      ))}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {structuredData}
        </script>
      )}
      
      {/* Additional SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Favicon and App Icons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />
      <link rel="manifest" href="/manifest.json" />
      
      {/* Theme Color */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      
      {/* DNS Prefetch for Performance */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//res.cloudinary.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      
      {/* Preconnect for Critical Resources */}
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="preconnect" href="https://res.cloudinary.com" />
    </Helmet>
  );
};

export default SEOHead;
